import React from 'react'
import Dialog from 'components/dialog'
import Button from '@material-ui/core/Button'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import Skeleton from '@material-ui/lab/Skeleton'
import InfoAlert from 'components/info-alert'
import Container from '@material-ui/core/Container'
import Typography from '@material-ui/core/Typography'
import { useGroupEventTemplateOptions } from 'components/group-events/form-option-hooks'
import { makeStyles } from '@material-ui/core/styles'
import { useSupportContactActions } from 'lib/support'
import { PROMO_CALLOUT_BORDER_RADIUS } from 'lib/form-field-styles'
import GroupEventTemplateCard from './group-event-template-card'
import { useIsMobile } from 'lib/hooks'

const TEMPLATE_SKELETON_COUNT = 6

const useStyles = makeStyles(theme => ({
  paper: {
    borderRadius: PROMO_CALLOUT_BORDER_RADIUS,
  },
  dialogTitle: {
    padding: theme.spacing(4),
    paddingRight: theme.spacing(7),
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 600,
    lineHeight: 1.3,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
  },
  subtitle: {
    fontSize: '1rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
  },
  dialogContent: {
    padding: theme.spacing(4),
    minHeight: 320,
  },
  container: {
    marginTop: theme.spacing(2),
  },
  suggestionCallout: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(4),
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
  },
  templateSkeleton: {
    borderRadius: PROMO_CALLOUT_BORDER_RADIUS,
  },
}))

const GroupEventTemplatesList = ({ onCreateClick }) => {
  const classes = useStyles()
  const { groupEventTemplates, loading } = useGroupEventTemplateOptions()

  if (loading) {
    return (
      <>
        {Array.from({ length: TEMPLATE_SKELETON_COUNT }).map((_, index) => (
          <Grid key={index} item lg={4} md={6} xs={12}>
            <Skeleton
              variant="rect"
              height={400}
              className={classes.templateSkeleton}
            />
          </Grid>
        ))}
      </>
    )
  }

  return (
    <>
      {groupEventTemplates.map(template => (
        <Grid key={template.title} item lg={4} md={6} xs={12}>
          <GroupEventTemplateCard
            event={template}
            callToAction="Use Template"
            onCreateClick={onCreateClick}
            isTemplate
          />
        </Grid>
      ))}
    </>
  )
}

const CreateFromTemplateDialog = ({
  trigger,
  isOpen,
  onClose,
  onAfterSelect,
}) => {
  const classes = useStyles()
  const isMobile = useIsMobile()
  const { startSupportSurvey } = useSupportContactActions()

  return (
    <Dialog
      trigger={trigger}
      isOpen={isOpen}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      showCloseButton
      classes={{ paper: classes.paper }}
    >
      {({ closeDialog }) => {
        return (
          <>
            <DialogTitle className={classes.dialogTitle} disableTypography>
              <Typography className={classes.title} component="h1">
                Create From Template
              </Typography>
              <Typography className={classes.subtitle}>
                Select a template to get started. As you progress through the
                event creation wizard, feel free to edit the event information
                as you see fit.
              </Typography>
            </DialogTitle>
            <DialogContent dividers className={classes.dialogContent}>
              <Container maxWidth="lg">
                <Grid container spacing={4}>
                  <GroupEventTemplatesList
                    onCreateClick={() => {
                      closeDialog()
                      onAfterSelect?.()
                    }}
                  />
                </Grid>
              </Container>
              <Container maxWidth="md" className={classes.container}>
                <Box mt={4}>
                  <InfoAlert
                    variant="promo"
                    className={classes.suggestionCallout}
                    style={{ backgroundColor: '#E0EAFF' }}
                    title="Don't see what you're looking for?"
                    action={
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                          startSupportSurvey(27771605)
                          closeDialog && closeDialog()
                        }}
                      >
                        Make A Suggestion
                      </Button>
                    }
                  >
                    <Typography variant="body2">
                      Make a suggestion on a popular event that you would like
                      to see featured. Check back often as we continue to add
                      new event templates.
                    </Typography>
                  </InfoAlert>
                </Box>
              </Container>
            </DialogContent>
          </>
        )
      }}
    </Dialog>
  )
}

export default CreateFromTemplateDialog
